/**
 * R10 Flag button (and, where `hotkey` is set, Alt+Shift+F): capture this moment of the game and copy the evidence line.
 * Beside the anchor strip on the ticket, on the /live strip, and in the Desk drawer. The trading hotkeys ignore Alt
 * (Lt2HotkeyListener), and a key typed into a field is never a flag.
 */
import { useEffect, useRef } from 'react';
import { Flag } from 'lucide-react';
import type { PenelopeGame } from '@/hooks/usePenelope';
import { usePenelopeFlag } from '@/hooks/usePenelopeFlag';
import { cn } from '@/lib/infra/utils';

/** True when a key went to something the trader is typing in. */
export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable === true;
}

/** Alt+Shift+F, by physical key (a layout's Alt+F character varies). */
export const isFlagChord = (e: Pick<KeyboardEvent, 'altKey' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'code'>): boolean =>
  e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyF';

export function PenelopeFlagButton({ row, status, source, hotkey = false, className }: {
  row: PenelopeGame | null | undefined; status?: string; source: string; hotkey?: boolean; className?: string;
}) {
  const f = usePenelopeFlag(row?.id);
  const running = Boolean(row?.live);
  // The latest status and flag call for the hotkey handler, without re-binding it every render.
  const statusRef = useRef(status);
  const flagRef = useRef(f.flag);
  useEffect(() => {
    statusRef.current = status;
    flagRef.current = f.flag;
  });
  useEffect(() => {
    if (!hotkey || !running) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isFlagChord(e) || e.repeat || isTypingTarget(e.target)) return;
      e.preventDefault();
      void flagRef.current({ status: statusRef.current, source: `${source}:hotkey` });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hotkey, running, source]);
  if (!running) return null;
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)}>
      <button
        type="button"
        disabled={f.busy}
        onClick={() => void f.flag({ status, source })}
        title={`Flag this moment: saves what she sees now and copies an evidence line (game / score and clock / PT time / what she is doing)${hotkey ? ' — Alt+Shift+F' : ''}`}
        className="border-border text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 rounded border px-1 py-px text-[10px] font-semibold disabled:opacity-50"
      >
        <Flag className="h-3 w-3" /> Flag
      </button>
      {f.line && <span className="text-muted-foreground truncate font-mono text-[9px]" title={f.line}>{f.copied ? 'copied: ' : ''}{f.line}</span>}
      {f.error && <span className="truncate text-[9px] text-amber-500" title={f.error}>{f.error}</span>}
    </span>
  );
}
